import React, { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useUpdateEffect } from "react-use";
import { Button, Caption1, ConfirmDialog, LayerContextProvider, layers } from "@activecollab/components";
import { List } from "immutable";
import PropTypes from "prop-types";
import UserLink from "~/components/UserLink/UserLink";
import { keyCodes } from "~/utils/key_codes.jsx";
import AngDropFiles from "../component.ang_drop_files.jsx";
import { useWhoIsWriting } from "../Hooks/index.jsx";
import { useFileHandler } from "../Hooks/useFileHandler.jsx";
import { AttachmentUpload } from "../select_attachments/AttachmentUpload.jsx";
import { SelectAttachments } from "../select_attachments/SelectAttachments.jsx";
import VisualEditorContainer from "../VisualEditor/VisualEditorContainer.jsx";

const CommentForm = ({
  id,
  body,
  bodyMode: defaultBodyMode,
  uploadedFiles: defaultUploadedFiles,
  dropAttachedFiles: defaultDropAttachedFiles,
  onSubmit,
  onCancel,
  onChange,
  createdById,
  createdByName,
  submitLabel,
  cancelLabel,
  attachments,
  parentType,
  parentId,
  projectId,
  dirty: defaultDirty,
}) => {
  const { stopWriting, startWriting } = useWhoIsWriting(`comment-${parentType}-${parentId}`);
  const submitButtonRef = useRef();
  const cancelButtonRef = useRef();
  const [confirmChanges, setConfirmChanges] = useState(false);
  const [state, setState] = useState(() => ({
    editorTextValue: body,
    bodyMode: defaultBodyMode,
    attachUploadedFiles: defaultUploadedFiles,
    dropAttachedFiles: defaultDropAttachedFiles,
    dirty: defaultDirty,
  }));

  const fileInputRef = useRef(null);

  const handleOpenFileWindow = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const { t } = useTranslation();

  const {
    uploads,
    uploading,
    uploaded,
    onUpload,
    onCancelUpload,
    onRetryUpload,
    onAttachFromDropbox,
    onAttachFromGoogleDrive,
    error,
  } = useFileHandler();

  const { editorTextValue, bodyMode, attachUploadedFiles, dropAttachedFiles, dirty } = state;

  useUpdateEffect(() => {
    typeof onChange === "function" &&
      onChange({
        body: editorTextValue,
        attach_uploaded_files: attachUploadedFiles,
        drop_attached_files: dropAttachedFiles,
        attachments,
      });
  }, [editorTextValue, attachUploadedFiles, dropAttachedFiles]);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();

      stopWriting();
      let newBody = ``;

      if (editorTextValue !== `&nbsp;`) {
        newBody = editorTextValue;
      }

      if (newBody.length && !uploading) {
        typeof onSubmit === "function" &&
          onSubmit(id, {
            body: newBody,
            attach_uploaded_files: attachUploadedFiles,
            drop_attached_files: dropAttachedFiles,
          });
      }
    },
    [stopWriting, editorTextValue, uploading, onSubmit, id, attachUploadedFiles, dropAttachedFiles]
  );

  const hideCommentForm = useCallback(() => {
    stopWriting();
    const bodyChanged = !!(editorTextValue && editorTextValue.length && editorTextValue !== `&nbsp;` && dirty);
    const uploadedFilesChanged = !!(attachUploadedFiles && attachUploadedFiles.length && dirty);

    if (bodyChanged || uploadedFilesChanged) {
      setConfirmChanges(true);
    } else {
      typeof onCancel === "function" && onCancel();
    }
  }, [stopWriting, editorTextValue, dirty, attachUploadedFiles, onCancel]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === keyCodes.enter && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      submitButtonRef?.current?.click();
    }
  }, []);

  const handleFormKeyDown = useCallback((e) => {
    if (e.key === keyCodes.escape) {
      e.preventDefault();
      cancelButtonRef?.current?.click();
    }
  }, []);

  const handleBodyChange = useCallback(
    (body) => {
      if (body?.length > 0) {
        startWriting();
      }
      setState((prevState) => ({
        ...prevState,
        editorTextValue: body,
        dirty: true,
      }));
    },
    [startWriting]
  );

  const handleChangeSelectedAttachments = useCallback(({ filesToAttach, filesToDrop }) => {
    setState((prevState) => ({
      ...prevState,
      attachUploadedFiles: filesToAttach,
      dropAttachedFiles: filesToDrop,
      dirty: true,
    }));
  }, []);

  const handleCancelConfirmation = useCallback(() => setConfirmChanges(false), []);

  const handleUpload = useCallback(
    (files) => {
      for (let i = 0; i < files.length; i++) {
        onUpload(files[i]);
      }
    },
    [onUpload]
  );

  return (
    <>
      <form
        className="object_comments_form"
        onKeyDown={handleFormKeyDown}
        onSubmit={handleSubmit}
      >
        <UserLink
          hideName
          id={createdById}
          name={createdByName}
          size={32}
          className="object_comments_form_avatar"
          context="comment - comment form"
        />
        <div className="object_comments_real_controls">
          <div className="object_comments_form_wrapper tw-relative">
            <VisualEditorContainer
              projectId={projectId}
              parentId={parentId}
              parentType={parentType}
              content={editorTextValue}
              bodyMode={bodyMode}
              placeholder={`${t(`Write a comment or press "/" for commands`)}...`}
              onChange={handleBodyChange}
              onKeyDown={handleKeyDown}
              autoFocus
            />
            <SelectAttachments
              error={error}
              uploads={uploads}
              uploaded={uploaded}
              onAttachFromDropbox={onAttachFromDropbox}
              onAttachFromGoogleDrive={onAttachFromGoogleDrive}
              onCancelUpload={onCancelUpload}
              onRetryUpload={onRetryUpload}
              attachmentIds={attachments}
              filesToAttach={attachUploadedFiles}
              filesToDrop={dropAttachedFiles}
              onChange={handleChangeSelectedAttachments}
              className="object_comments_attachments"
              onOpenFileWindow={handleOpenFileWindow}
            />
            <AngDropFiles
              onDrop={handleUpload}
              className="comment_form_drop_files"
            />
          </div>

          <div className="object_comments_form_buttons">
            <Caption1 className="note fvl_aid tw-my-3 tw-text-theme-700">
              {t("To notify others, use @name @team-name")} @Here @Everyone
            </Caption1>
            <Button
              ref={submitButtonRef}
              className="tw-mr-2"
              disabled={uploading || !editorTextValue.length}
            >
              {!uploading ? (submitLabel ? submitLabel : t("Save")) : `${t("Uploading")}...`}
            </Button>
            <Button
              ref={cancelButtonRef}
              type="button"
              variant="secondary"
              onClick={hideCommentForm}
            >
              {cancelLabel ? cancelLabel : t("Cancel")}
            </Button>
          </div>
        </div>
        <AttachmentUpload
          ref={fileInputRef}
          multiple
          onUpload={onUpload}
        />
      </form>
      <LayerContextProvider value={{ zIndex: layers.skyscraper }}>
        <ConfirmDialog
          open={confirmChanges}
          dialogTitle={t("Discard changes?")}
          dialogContent={t("All unsaved changes will be lost.")}
          confirmBtnText={t("Discard")}
          onConfirm={onCancel}
          onCancel={handleCancelConfirmation}
        />
      </LayerContextProvider>
    </>
  );
};

CommentForm.propTypes = {
  id: PropTypes.any,
  body: PropTypes.string,
  dropAttachedFiles: PropTypes.array,
  attachments: PropTypes.instanceOf(List),
  uploadedFiles: PropTypes.array,
  createdById: PropTypes.number,
  createdByName: PropTypes.string,
  submitLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  onChange: PropTypes.func,
  parentType: PropTypes.string,
  parentId: PropTypes.number,
  projectId: PropTypes.number,
  bodyMode: PropTypes.string,
  dirty: PropTypes.bool,
};

CommentForm.defaultProps = {
  id: 0,
  body: ``,
  dropAttachedFiles: [],
  attachments: List(),
  uploadedFiles: [],
  parentType: "",
  parentId: 0,
  projectId: 0,
  bodyMode: "paragraph",
  dirty: false,
};

export default CommentForm;
